import { Link, NavLink, useLocation } from "react-router-dom";

import Logo, { BrandMark } from "../common/Brand";
import {
  DashboardGlyph,
  ReportGlyph,
  CampaignGlyph,
  AdvertiserGlyph,
  PublisherGlyph,
  BillingGlyph,
  PostbackGlyph,
  ProfileGlyph,
} from "../common/icons";

import {
  LogoContainer,
  Menu,
  SidebarContainer,
  SidebarFooter,
} from "./SidebarStyle";

// One entry per nav item. `match` lists the extra route prefixes that should
// keep the item highlighted (create/edit/view screens live on their own paths).
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      {
        to: "/",
        label: "Dashboard",
        Icon: DashboardGlyph,
        exact: true,
      },
      {
        to: "/reports",
        label: "Report and analytics",
        Icon: ReportGlyph,
        match: [
          "/view-report/",
          "/publisher-campaigns/",
          "/publisher-details/",
          "/campaign-details/",
          "/campaign-publishers/",
        ],
      },
    ],
  },
  {
    label: "Manage",
    items: [
      {
        to: "/campaigns",
        label: "Campaigns",
        Icon: CampaignGlyph,
        match: [
          "/create-campaign",
          "/edit-campaign/",
          "/view-campaign/",
          "/campaign-link/",
        ],
      },
      {
        to: "/advertisers",
        label: "Advertisers",
        Icon: AdvertiserGlyph,
        match: ["/create-advertiser", "/edit-advertiser/", "/view-advertiser/"],
      },
      {
        to: "/publishers",
        label: "Publishers",
        Icon: PublisherGlyph,
        match: [
          "/create-publisher",
          "/edit-publisher/",
          "/view-publisher/",
          "/publisher-link",
        ],
        hideForSupport: true,
      },
    ],
  },
  {
    label: "Revenue",
    items: [
      {
        to: "/billing",
        label: "Billing and payments",
        Icon: BillingGlyph,
        hideForSupport: true,
      },
      {
        to: "/postback",
        label: "Postback management",
        Icon: PostbackGlyph,
      },
    ],
  },
  {
    label: "Account",
    items: [
      {
        to: "/profile",
        label: "Profile",
        Icon: ProfileGlyph,
        match: ["/create-member", "/edit-member/"],
        hideForSupport: true,
      },
    ],
  },
];

const isItemActive = (item, path) => {
  if (item.exact ? path === item.to : path.startsWith(item.to)) return true;
  return (item.match || []).some((prefix) => path.startsWith(prefix));
};

const Sidebar = ({ forwardRef, collapsed = false, onNavigate }) => {
  const { pathname } = useLocation();
  const userRole = localStorage.getItem("userRole");

  return (
    <SidebarContainer ref={forwardRef} $collapsed={collapsed}>
      <LogoContainer $collapsed={collapsed}>
        <Link to="/" aria-label="ClickCircle home">
          {collapsed ? <BrandMark size={30} /> : <Logo size={30} />}
        </Link>
      </LogoContainer>

      <Menu $collapsed={collapsed}>
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter(
            (item) => !(item.hideForSupport && userRole === "SUPPORT")
          );
          if (!items.length) return null;

          return (
            <div key={group.label}>
              <p className="group-label">{group.label}</p>
              <ul>
                {items.map((item) => {
                  const active = isItemActive(item, pathname);
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.exact}
                        className={active ? "active" : ""}
                        title={collapsed ? item.label : undefined}
                        onClick={onNavigate}
                      >
                        <item.Icon />
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </Menu>

      <SidebarFooter $collapsed={collapsed}>
        <h4>ClickCircle Admin</h4>
        <p>Track campaigns, publishers and payouts in one place.</p>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar;
